import { callAjaxIO } from 'Shared/ajax_io';
import { onChildWindowClose } from 'Shared/utils/on-child-window-close';
import { Header } from 'Shared/header';
import { CookieUtils } from 'Shared/cookies/cookie-utils';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { loginAnalyticsEvent } from 'Shared/login-analytics-event';
import { SentryError } from '@ifixit/sentry';

const updateLoginState = details => {
   if (details.loggedIn) {
      Auth.loggedIn(details);
   } else {
      Auth.loggedOut(details);
   }
};

const parseJsonOrNull = text => {
   try {
      return JSON.parse(text);
   } catch {
      return null;
   }
};

// Callbacks run before the promise settles: LogIn.tsx reads the resolved body
// after its handler ran. A non-JSON error body skips onFailure.
const postToApi = async (endpoint, statName, data, { onSuccess, onFailure }) => {
   // A static import adds 18 KiB to the guide page bundle; only the login
   // and join forms call this.
   const { IFixitAPIClient } = await import('@ifixit/ifixit-api-client');
   const client = new IFixitAPIClient({
      origin: window.location.origin,
      headers: { 'Api-Client': 'iFixit-Web', 'Content-Type': 'application/json' },
   });
   const response = await client.postRaw(endpoint, statName, {
      body: JSON.stringify(data),
      // Join.tsx hears back only through the handler, which a timeout skips,
      // so allow a slow request a minute instead of the client's 10 seconds.
      retry: { globalTimeout: 60_000 },
   });
   const text = await response.text();
   if (response.ok) {
      const body = text ? JSON.parse(text) : null;
      onSuccess(body);
      return body;
   }
   const body = parseJsonOrNull(text);
   if (body && onFailure) {
      onFailure(body);
   }
   throw new SentryError(body?.message ?? `${endpoint} failed with status ${response.status}`);
};

/**
 * @typedef {(...a: any)=>any} fn
 * @type {{
 *    required: (options) => any,
 *    isLoggedIn: () => boolean,
 * }}
 */
export const Auth = (window.Auth = {
   protocol: window.location.protocol.slice(0, -1),

   defaultOptions: {
      from: 'navLogin',
      onAuthorize: () => {},
      close: true,
      reload: false,
      locked: false, // locked and noOverlay are used at /Login
      noOverlay: false,
      stayInWindow: false,
      register: true,
   },

   login: function (options) {
      options = { ...this.defaultOptions, ...options };

      if (this.trySSOLogin(options)) {
         return;
      }

      const inviteData = document.querySelector('#inviteData');
      window.ReactLoginPortal.open({
         message: options.message,
         email: inviteData ? inviteData.dataset.email : undefined,
         onSuccess: () => {
            options.onAuthorize();
            if (options.reload) {
               window.location.reload();
            }
         },
         onCancel: options.onCancel,
      });
   },

   logout: () => {
      window.location = window.shared_constants.GuideURI('TAG_LOGOUT');
   },

   trySSOLogin: function (options) {
      let ssoURL = App['single-sign-on-url'],
         ignoreSSO = options && options.ignoreSSO,
         cookieOptions = {
            maxAgeInSeconds: 840,
         };

      // Bail out early if we shouldn't use SSO
      if (App['multi-login'] || !ssoURL || ignoreSSO) {
         return false;
      }

      ssoURL = options.register ? ssoURL.register : ssoURL.login;

      // If we are supposed to reload the page after logging in use the
      // current window for the SSO redirecting
      if (options.reload || options.stayInWindow) {
         CookieUtils.set('sso-origin', window.location.href, cookieOptions);
         window.location = ssoURL;

         // Otherwise, do the SSO login in a new window
      } else {
         // Set the origin to a special url that temote_login checks for and
         // instead of redirecting, will just close the window.
         CookieUtils.set(
            'sso-origin',
            window.shared_constants.GuideURI('TAG_SSO_CLOSE_WINDOW'),
            cookieOptions
         );

         const loginWindow = window.open(ssoURL, 'sso_window');

         onChildWindowClose(loginWindow, closed => {
            // if the window wasn't closed, then it timed out and we should
            // ignore it.
            if (!closed) {
               return;
            }

            fetch(window.shared_constants.GuideURI('TAG_USER_DETAILS'))
               .then(response => (response.ok ? response.json() : null))
               .then(details => details && updateLoginState(details))
               // The MooTools request ignored a failed lookup too.
               .catch(() => {});
         });
      }

      return true;
   },

   // Nothing sets cookies for it to clear, but LogIn.tsx, Email.tsx,
   // FacebookSSOButton.tsx and GuideLoginPageLib::facebook() still call it.
   clearCookies: function () {},

   sendFacebookLogin: function (handler) {
      this.onComplete = handler;

      // meta and www share the same session cookie.
      let domain = window.location.toString().replace(/^[^/]+\/\/([^/]+).*$/, '$1');
      domain = domain.replace(/^meta\./, 'www.');

      const script = document.createElement('script');
      script.src =
         this.protocol +
         '://' +
         domain +
         window.shared_constants.GuideURI('TAG_LOGIN') +
         '/facebookLogin?' +
         Math.floor(Math.random() * 1_000_000_000);
      document.body.append(script);
   },

   sendLogin: function (options) {
      this.onComplete = options.handler;

      let login = options.login,
         password = options.password,
         googleOAuthData = options.googleOAuthData,
         googleOAuthTokenCode = null;
      if (googleOAuthData) {
         googleOAuthTokenCode = googleOAuthData.credential;
      }

      const data = {
         login: login,
         password: password,
         googleOAuthToken: googleOAuthTokenCode,
         captchaResult: options.captchaResult,
      };

      return callAjaxIO('login', data).then(response => {
         response.login = login;
         this.responseHandler(response);
         return response;
      });
   },

   validateLogin: (email, handler) =>
      postToApi(
         'user/login/validateLogin',
         'login-validate',
         { login: email },
         {
            onSuccess: response => {
               handler({
                  hasAccount: response.has_account,
                  ssoAssociation: response.sso_association,
               });
            },
         }
      ),

   sendGetLoginCode: function (formValues, handler) {
      this.onComplete = handler;

      // Set the captcha to a random value for tests.
      // if (!formValues.captchaResult) {
      //    formValues.captchaResult = '1234';
      // }

      return postToApi(
         'user/login/getLoginCode',
         'login-get-code',
         {
            login: formValues.login,
            captchaResult: formValues.captchaResult,
         },
         {
            onSuccess: () => {
               this.responseHandler({
                  loggedIn: false,
                  emailSent: true,
                  from: 'getLoginCode',
               });
            },
            onFailure: response => {
               this.responseHandler({ ...response, loggedIn: false, emailSent: false });
            },
         }
      );
   },

   sendGetRegisterCode: function (formValues, handler) {
      this.onComplete = handler;

      return postToApi(
         'user/login/getRegisterCode',
         'register-get-code',
         {
            login: formValues.login,
            captchaResult: formValues.captchaResult,
         },
         {
            onSuccess: () => {
               this.responseHandler({
                  loggedIn: false,
                  emailSent: true,
                  from: 'getRegisterCode',
               });
            },
            onFailure: response => {
               this.responseHandler({ ...response, loggedIn: false, emailSent: false });
            },
         }
      );
   },

   sendLoginWithCode: function (formValues, handler) {
      this.onComplete = handler;

      // Set the captcha to a random value for tests.
      // if (!formValues.captchaResult) {
      //   formValues.captchaResult = '1234';
      // }

      return postToApi(
         'user/login/loginWithCode',
         'login-with-code',
         {
            login: formValues.login,
            code: formValues.code,
            // TODO: pass captcha?
            // captchaResult: formValues.captchaResult,
         },
         {
            onSuccess: response => {
               this.responseHandler({
                  loggedIn: true,
                  newUser: false,
                  from: 'loginWithCode',
                  username: response.username,
               });
            },
            onFailure: response => {
               this.responseHandler({ ...response, loggedIn: false });
            },
         }
      );
   },

   registerWithCode: function (formValues, handler) {
      this.onComplete = handler;

      // Set the captcha to a random value for tests.
      if (!formValues.captchaResult) {
         formValues.captchaResult = '1234';
      }

      return postToApi(
         'user/login/registerWithCode',
         'register-with-code',
         {
            login: formValues.login,
            username: formValues.username,
            code: formValues.code,
            inviteCode: formValues.inviteCode,
            wantsDigestEmails: formValues.wantsDigestEmails,
            wantsNewsEmails: formValues.wantsNewsEmails,
            captchaResult: formValues.captchaResult,
         },
         {
            onSuccess: response => {
               this.responseHandler({
                  loggedIn: true,
                  newUser: true,
                  from: 'registerWithCode',
                  username: response.username,
               });
            },
            onFailure: response => {
               this.responseHandler({ ...response, loggedIn: false });
            },
         }
      );
   },

   required: function (options) {
      if (!this.isLoggedIn()) {
         if (options.reload) {
            options.onAuthorize = () => {};
         }

         window.ReactLoginPortal.open({
            message: options.message,
            onSuccess: () => {
               options.onAuthorize();
               if (options.reload) {
                  window.location.reload();
               }
            },
            onCancel: options.onCancel,
            register: options.register,
         });
         return false;
      } else if (options.onAuthorize) {
         options.onAuthorize();
      }
      return true;
   },

   responseHandler: function (details) {
      updateLoginState(details);
      if (this.onComplete) {
         this.onComplete(details);
      }
   },

   loggedIn: info => {
      Header.showLoggedIn(info);
      App.isLoggedIn = true;

      const analyticsEvent = loginAnalyticsEvent(info);
      if (analyticsEvent) {
         trackAnalyticsCustomEvent(analyticsEvent);
      }
   },

   loggedOut: info => {
      Header.showLoggedOut();
      App.isLoggedIn = false;
      /**
       * Simple hack to force the user to go to the home page when logging
       * out on specific pages. The real fix would be to pass in a funcion
       * for log out, but Shawn should handle that and he's too busy.
       *
       * To add a new page add a unique identifer from the URL to the array.
       */
      const location = window.location.href;
      ['edit-profile', 'view-profile'].forEach(item => {
         if (location.includes(item)) {
            window.location = window.shared_constants.GuideURI('TAG_SITE_HOMEPAGE');
         }
      });
      if (info && info.reload) {
         window.location.reload();
      }
   },

   isLoggedIn: () => App.isLoggedIn,
});
